import { Listbox, Transition } from "@headlessui/react";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import clsx from "clsx";
import { Fragment, ReactElement, useState } from "react";
import { FieldError } from "react-hook-form";
import FormInputMessage from "../FormInputError";
import FormInputLabel from "../FormInputLabel";
import InfoCard from "../InfoCard";
import {
    DropdownPosition,
    FormDropdownOption,
    FormDropdownOptionsWithSections,
} from "./types";
import { buildOptionsList } from "./formDropdownUtils";
import React from "react";

export type FormDropdownProps<TValue extends string | number> = {
    options:
        | FormDropdownOption<TValue>[]
        | FormDropdownOptionsWithSections<TValue>;
    label?: string;
    pleaseSelectText?: string;
    value?: TValue;
    onChange: (value: TValue, option: FormDropdownOption<TValue>) => void;
    error?: FieldError;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    infoText?: string;
    isLoadingOptions?: boolean;
    position?: DropdownPosition;
    hideLabel?: boolean;
    inputClassName?: string;
    selectedTextWrapper?: (selected: string) => ReactElement;
    buildSelectedTextOverride?: (optionId: TValue) => string | undefined;
};

const FormDropdown = <TValue extends string | number>({
    label,
    pleaseSelectText = "Select an option",
    options,
    value,
    onChange,
    error,
    required,
    className,
    infoText,
    disabled,
    isLoadingOptions,
    position = "bottom",
    hideLabel = false,
    inputClassName,
    selectedTextWrapper,
    buildSelectedTextOverride,
}: FormDropdownProps<TValue>) => {
    const [moreInfoIsExpanded, setMoreInfoIsExpanded] = useState(false);
    const moreInfoLabelId = `${label}-more-info`;
    const moreInfoPanelId = `${label}-more-info-panel`;
    const _disabled = disabled || isLoadingOptions;

    const optionsList = buildOptionsList(options);

    // Below is not ideal but works for now. Will need to improve if we want to have none selected state when TValue is string.
    const valueIsNumber = typeof value === "number";
    const selected =
        value !== undefined && (!valueIsNumber || value >= 0)
            ? optionsList.find((option) => option.value === value)
            : undefined;

    if (!isLoadingOptions && value && value !== -1 && !selected)
        throw Error(`Couldn't find option with value ${value}.`);

    const handleChange = (value: TValue) => {
        const option = optionsList.find((_) => _.value === value);

        if (!option) throw new Error(`Expected option with value ${value}.`);

        onChange(value, option);
    };

    let selectedText = pleaseSelectText;

    if (selected) {
        selectedText = selected.label;
    }

    if (buildSelectedTextOverride && selected) {
        const selectedTextOverride = buildSelectedTextOverride(selected?.value);

        if (selectedTextOverride !== undefined) {
            selectedText = selectedTextOverride;
        }
    }

    if (isLoadingOptions) {
        selectedText = "Loading...";
    }

    const buildOptionComponent = (option: FormDropdownOption<TValue>) => {
        return (
            <Listbox.Option
                key={option.value}
                className={({ active, disabled }) =>
                    clsx(
                        "relative cursor-default select-none py-3 px-4 transition-colors hover:bg-soft-green-80",
                        active && "bg-soft-green-80",
                        disabled && "opacity-40"
                    )
                }
                value={option.value}
                disabled={option.disabled}
            >
                <span className="block truncate">{option.label}</span>
            </Listbox.Option>
        );
    };

    const buildOptionComponents = () => {
        if (Array.isArray(options)) {
            return optionsList.map((option) => buildOptionComponent(option));
        }

        return Object.keys(options)
            .sort((a, b) => options[a].order - options[b].order)
            .map((sectionTitle) => (
                <React.Fragment key={sectionTitle}>
                    <optgroup
                        label={sectionTitle}
                        className={clsx(
                            "text-xs leading-4 font-semibold p-2 pb-0"
                        )}
                    />
                    {options[sectionTitle].options.map(buildOptionComponent)}
                </React.Fragment>
            ));
    };

    return (
        <div className={className}>
            {label && (
                <div className="flex justify-between">
                    <FormInputLabel
                        className={clsx(
                            "whitespace-nowrap",
                            hideLabel && "sr-only"
                        )}
                        required={required}
                    >
                        {label}
                    </FormInputLabel>
                    {infoText && (
                        <button
                            className="text-sm underline"
                            id={moreInfoLabelId}
                            aria-expanded={moreInfoIsExpanded}
                            aria-controls={moreInfoPanelId}
                            onClick={() =>
                                setMoreInfoIsExpanded((prev) => !prev)
                            }
                            aria-label={
                                moreInfoIsExpanded ? "hide info" : undefined
                            }
                            type="button"
                        >
                            <span className="sr-only">{label} </span>info
                        </button>
                    )}
                </div>
            )}
            <Listbox value={value} onChange={handleChange} disabled={_disabled}>
                {({ open }) => (
                    <div className="relative">
                        <Listbox.Button
                            className={clsx(
                                "border relative text-left cursor-default border-surface-dark-5 w-full h-[3.25rem] px-4 rounded transition-colors pr-10",
                                "focus:bg-surface-light focus:outline-none focus:ring-2 focus:ring-soft-green-80",
                                !disabled && "hover:bg-surface-light-1",
                                open && "z-50 ring-2 ring-soft-green-80",
                                !error && "bg-surface-light",
                                error &&
                                    "bg-red-95 outline-red-40 border-red-40 focus:ring-red-40 ring-red-40 focus:outline outline outline-2 pr-10",
                                open && error && "ring-0",
                                inputClassName,
                                _disabled && "border-disabled opacity-50"
                            )}
                        >
                            <span
                                className={clsx(
                                    "block truncate",
                                    !selected && "text-placeholder-text",
                                    _disabled && "text-disabled-text"
                                )}
                            >
                                <span className="flex items-center">
                                    {selectedTextWrapper ? (
                                        selectedTextWrapper(selectedText)
                                    ) : (
                                        <>{selectedText}</>
                                    )}
                                </span>
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                                {open ? (
                                    <CaretUp
                                        className={clsx(
                                            "h-5 w-5 text-inherit",
                                            _disabled && "text-disabled-text"
                                        )}
                                        aria-hidden
                                    />
                                ) : (
                                    <CaretDown
                                        className={clsx(
                                            "h-5 w-5 text-inherit",
                                            _disabled && "text-disabled-text"
                                        )}
                                        aria-hidden
                                    />
                                )}
                            </span>
                        </Listbox.Button>
                        <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Listbox.Options
                                className={clsx(
                                    "drop-shadow-lg rounded-xl absolute max-h-60 w-full overflow-auto",
                                    "scrollbar-thin scrollbar-thumb-scrollbar bg-white text-base",
                                    "focus:outline-none sm:text-sm z-50",
                                    position === "top" && "bottom-full mb-1",
                                    position === "bottom" && "mt-1",
                                    open && position === "bottom" && "border-t",
                                    open && position === "top" && "border-t"
                                )}
                            >
                                {buildOptionComponents()}
                            </Listbox.Options>
                        </Transition>
                    </div>
                )}
            </Listbox>
            <FormInputMessage error={error} />
            {infoText && (
                <InfoCard
                    className="p-3 mt-4"
                    id={moreInfoPanelId}
                    aria-labelledby={moreInfoLabelId}
                    hidden={!moreInfoIsExpanded}
                >
                    {label && <h3 className="font-bold mb-1">{label}</h3>}
                    {infoText}
                </InfoCard>
            )}
        </div>
    );
};

export default FormDropdown;
